/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Knowledge type.
 */
export const MemoryType = {
  Glossary: "glossary",
  Procedure: "procedure",
  Result: "result",
} as const;
/**
 * Knowledge type.
 */
export type MemoryType = ClosedEnum<typeof MemoryType>;

/**
 * Knowledge extracted from a completed business session.
 */
export type BusinessMemory = {
  /**
   * Normalized knowledge body.
   */
  body: string;
  /**
   * Canonical entity and topic labels.
   */
  contentScope: Array<string>;
  /**
   * Model used to create the semantic-search vector.
   */
  embeddingModel: string;
  /**
   * Extraction timestamp.
   */
  extractedAt: Date;
  /**
   * Model used to extract the memory.
   */
  extractionModel: string;
  /**
   * Memory ID.
   */
  id: string;
  /**
   * Current lifecycle state.
   */
  lifecycleState: string;
  /**
   * Knowledge type.
   */
  memoryType: MemoryType;
  /**
   * Cosine similarity for semantic-search results.
   */
  similarity?: number | undefined;
  /**
   * Author associated with the source session.
   */
  sourceAuthorId?: string | undefined;
  /**
   * Chat the memory was extracted from, or unavailable if the source chat was deleted.
   */
  sourceChatId: string;
  /**
   * One-based transcript turn cited by the extractor.
   */
  sourceTurn?: number | undefined;
  /**
   * Materialized structural scope path.
   */
  structuralScope: string;
};

/** @internal */
export const MemoryType$inboundSchema: z.ZodMiniEnum<typeof MemoryType> = z
  .enum(MemoryType);

/** @internal */
export const BusinessMemory$inboundSchema: z.ZodMiniType<
  BusinessMemory,
  unknown
> = z.pipe(
  z.object({
    body: z.string(),
    content_scope: z.array(z.string()),
    embedding_model: z.string(),
    extracted_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    extraction_model: z.string(),
    id: z.string(),
    lifecycle_state: z.string(),
    memory_type: MemoryType$inboundSchema,
    similarity: z.optional(z.number()),
    source_author_id: z.optional(z.string()),
    source_chat_id: z.string(),
    source_turn: z.optional(z.int()),
    structural_scope: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "content_scope": "contentScope",
      "embedding_model": "embeddingModel",
      "extracted_at": "extractedAt",
      "extraction_model": "extractionModel",
      "lifecycle_state": "lifecycleState",
      "memory_type": "memoryType",
      "source_author_id": "sourceAuthorId",
      "source_chat_id": "sourceChatId",
      "source_turn": "sourceTurn",
      "structural_scope": "structuralScope",
    });
  }),
);

export function businessMemoryFromJSON(
  jsonString: string,
): SafeParseResult<BusinessMemory, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => BusinessMemory$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'BusinessMemory' from JSON`,
  );
}
